import React from 'react';
import CourseTemplate from '../../components/CourseTemplate';

const RespiratoryTherapyCoursePage: React.FC = () => {
  const respiratoryData = {
    title: "Bachelor of Science in Respiratory Therapy",
    shortName: "B.Sc RT",
    duration: "3 Years",
    description: "A specialized healthcare program focusing on respiratory care, mechanical ventilation, and treatment of breathing disorders and pulmonary diseases.",
    overview: "B.Sc Respiratory Therapy prepares students to become respiratory therapists who assess, treat, and care for patients with breathing disorders and cardiopulmonary diseases. The program covers pulmonary function, mechanical ventilation, oxygen therapy, airway management, and patient assessment. Students learn to operate respiratory equipment, provide life support, and work with patients ranging from premature infants to elderly adults with chronic lung diseases.",
    eligibility: [
      "Completed 10+2 with Physics, Chemistry, and Biology",
      "Minimum 50% aggregate marks (45% for SC/ST/OBC)",
      "Age: 17-25 years",
      "Interest in respiratory healthcare",
      "Good manual dexterity",
      "Empathy for critically ill patients"
    ],
    careerProspects: {
      roles: [
        "Respiratory Therapist",
        "Pulmonary Function Technologist",
        "Sleep Study Technician",
        "Mechanical Ventilation Specialist",
        "Cardiac Pulmonary Rehabilitation Specialist",
        "Neonatal Respiratory Specialist",
        "Home Care Respiratory Therapist",
        "Respiratory Care Manager",
        "Equipment Specialist",
        "Research Coordinator"
      ],
      industries: [
        "Hospitals and ICUs",
        "Pulmonary Rehabilitation Centers",
        "Sleep Disorder Centers",
        "Home Healthcare Services",
        "Medical Equipment Companies",
        "Research Institutions",
        "Government Health Services",
        "Long-term Care Facilities",
        "Emergency Medical Services",
        "Educational Institutions"
      ],
      averageSalary: "₹3.5-8 LPA (varies by experience and specialization)",
      topRecruiters: [
        "AIIMS Pulmonology",
        "Apollo Hospitals",
        "Fortis Healthcare",
        "Medanta Hospital",
        "Max Healthcare",
        "Manipal Hospitals",
        "Narayana Health",
        "Philips Healthcare",
        "ResMed India",
        "Government Medical Colleges"
      ]
    },
    curriculum: [
      {
        year: 1,
        subjects: [
          "Human Anatomy and Physiology",
          "Respiratory Anatomy",
          "Basic Sciences",
          "Medical Terminology",
          "Microbiology",
          "Pathophysiology",
          "Patient Care Fundamentals",
          "Communication Skills"
        ]
      },
      {
        year: 2,
        subjects: [
          "Pulmonary Function Testing",
          "Mechanical Ventilation",
          "Oxygen Therapy",
          "Airway Management",
          "Respiratory Pharmacology",
          "Cardiopulmonary Pathology",
          "Equipment Operation",
          "Clinical Assessment"
        ]
      },
      {
        year: 3,
        subjects: [
          "Advanced Mechanical Ventilation",
          "Neonatal and Pediatric Care",
          "Pulmonary Rehabilitation",
          "Sleep Studies",
          "Critical Care",
          "Research Methods",
          "Professional Practice",
          "Clinical Internship"
        ]
      }
    ],
    admissionProcess: [
      "Apply to colleges offering B.Sc Respiratory Therapy",
      "Entrance examination (typically NEET-based)",
      "Merit-based selection",
      "Document verification",
      "Medical fitness certificate",
      "Fee payment and admission",
      "Orientation program",
      "Begin clinical and theoretical training"
    ],
    topColleges: [
      {
        name: "All India Institute of Medical Sciences",
        location: "New Delhi",
        fees: "₹15,000 PA",
        ranking: "Premier Medical Institute"
      },
      {
        name: "Christian Medical College",
        location: "Vellore, Tamil Nadu",
        fees: "₹3 LPA",
        ranking: "Premier Medical College"
      },
      {
        name: "Kasturba Medical College",
        location: "Manipal, Karnataka",
        fees: "₹4 LPA",
        ranking: "Deemed University"
      },
      {
        name: "JSS Academy of Higher Education",
        location: "Mysore, Karnataka",
        fees: "₹2.5 LPA",
        ranking: "Deemed University"
      },
      {
        name: "Amrita Institute of Medical Sciences",
        location: "Kochi, Kerala",
        fees: "₹3.5 LPA",
        ranking: "Private Medical College"
      }
    ],
    skills: [
      "Equipment Operation",
      "Patient Assessment",
      "Critical Thinking",
      "Manual Dexterity",
      "Communication Skills",
      "Emergency Response",
      "Technology Proficiency",
      "Attention to Detail",
      "Teamwork",
      "Continuous Learning",
      "Problem Solving",
      "Compassion"
    ],
    futureScope: "Respiratory therapy is expanding with advances in mechanical ventilation, home respiratory care, telemedicine monitoring, and specialized areas like neonatal care and sleep medicine. The COVID-19 pandemic has highlighted the critical importance of respiratory specialists. Future opportunities include specialization in artificial lung support, robotic ventilation, and respiratory care in space medicine.",
    faqs: [
      {
        question: "What is the role of a respiratory therapist?",
        answer: "Respiratory therapists assess, treat, and provide care for patients with breathing disorders. They operate life-support equipment, provide oxygen therapy, manage mechanical ventilators, and help patients improve lung function."
      },
      {
        question: "Is this field emotionally challenging?",
        answer: "Yes, respiratory therapists often work with critically ill patients and in life-or-death situations. However, the ability to help patients breathe and recover makes it highly rewarding. Proper training includes coping strategies."
      },
      {
        question: "What are the work environments like?",
        answer: "Work environments include hospital ICUs, emergency departments, sleep labs, rehabilitation centers, and patients' homes. The work can involve shift work including nights, weekends, and holidays."
      },
      {
        question: "Can I specialize in specific areas?",
        answer: "Yes, specializations include neonatal/pediatric respiratory care, sleep medicine, pulmonary rehabilitation, home care, and advanced mechanical ventilation for specific patient populations."
      },
      {
        question: "What certification is required for practice?",
        answer: "While certification requirements vary by state, many employers prefer certified respiratory therapists. Professional certification through organizations like AARC (American Association for Respiratory Care) enhances career prospects."
      }
    ]
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-black flex flex-col items-center justify-start py-8 px-2">
      <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 drop-shadow-glow mb-6 text-center animate-glow">
        {respiratoryData.title} ({respiratoryData.shortName})
      </h1>
      <div className="w-full max-w-4xl">
        <CourseTemplate courseData={respiratoryData} />
      </div>
    </div>
  );
};

export default RespiratoryTherapyCoursePage;